'use client';

import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, CalendarOff, CircleCheck, Clock, MessageSquare, RefreshCw } from 'lucide-react';
import type { EstadoPosSentenca, PrazoEsclarecimento } from '../types/encerramento.types';
import { formatarData, rotuloDiasRestantes } from '../utils/formatarEncerramento';
import { classesDoTom, type TomEncerramento } from '../utils/tonsEncerramento';
interface FaixaEstadoPosSentencaProps {
  readonly estado: EstadoPosSentenca;
  readonly prazo: PrazoEsclarecimento | null;
  readonly intimadasEm: string | null;
  readonly manifestacaoRegistradaEm?: string | null;
  readonly encerradoEm?: string | null;
  readonly carregando?: boolean;
  readonly erro?: string | null;
  readonly onTentarNovamente?: () => void;
}
interface Conteudo {
  readonly tom: TomEncerramento;
  readonly Icone: typeof Clock;
  readonly titulo: string;
  readonly detalhe: string;
  readonly selo: string | null;
}
export function FaixaEstadoPosSentenca(t0) {
  const $ = _c(49);
  const {
    estado,
    prazo,
    intimadasEm,
    manifestacaoRegistradaEm: t1,
    encerradoEm: t2,
    carregando: t3,
    erro: t4,
    onTentarNovamente
  } = t0;
  const manifestacaoRegistradaEm = t1 === undefined ? null : t1;
  const encerradoEm = t2 === undefined ? null : t2;
  const carregando = t3 === undefined ? false : t3;
  const erro = t4 === undefined ? null : t4;
  let conteudo;
  let t5;
  if ($[0] !== encerradoEm || $[1] !== estado || $[2] !== intimadasEm || $[3] !== manifestacaoRegistradaEm || $[4] !== prazo) {
    conteudo = montarConteudo({
      estado,
      prazo,
      intimadasEm,
      manifestacaoRegistradaEm,
      encerradoEm
    });
    t5 = classesDoTom(conteudo?.tom ?? "neutro");
    $[0] = encerradoEm;
    $[1] = estado;
    $[2] = intimadasEm;
    $[3] = manifestacaoRegistradaEm;
    $[4] = prazo;
    $[5] = conteudo;
    $[6] = t5;
  } else {
    conteudo = $[5];
    t5 = $[6];
  }
  const classes = t5;
  if (carregando) {
    let t6;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="h-8 w-8 animate-pulse rounded-[8px] bg-neutral-100" />;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div aria-busy="true" aria-label="Carregando estado do processo" className="bg-default-background flex items-center gap-3 rounded-[16px] border border-solid border-neutral-200 p-4">{t6}<div className="flex-1 space-y-2"><div className="h-3 w-64 animate-pulse rounded-md bg-neutral-100" /><div className="h-3 w-40 animate-pulse rounded-md bg-neutral-100" /></div></div>;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    return t7;
  }
  if (erro) {
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = classesDoTom("erro");
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    const tomErro = t6;
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] ${tomErro.icone}`}><AlertTriangle aria-hidden="true" className="h-5 w-5" /></div>;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <p className={`text-sm font-medium ${tomErro.texto}`}>Não conseguimos carregar o prazo deste processo</p>;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    let t9;
    if ($[12] !== erro) {
      t9 = <div className="min-w-0 flex-1">{t8}<p className="text-xs text-neutral-600">{erro}</p></div>;
      $[12] = erro;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    let t10;
    if ($[14] !== onTentarNovamente) {
      t10 = onTentarNovamente ? <button className="flex items-center gap-1.5 rounded-[8px] border border-neutral-300 px-3 py-1.5 text-xs text-neutral-700" onClick={onTentarNovamente} type="button"><RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />Tentar de novo</button> : null;
      $[14] = onTentarNovamente;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    let t11;
    if ($[16] !== t10 || $[17] !== t9) {
      t11 = <div className={`flex items-center gap-3 rounded-[16px] border p-4 ${tomErro.caixa}`} role="alert">{t7}{t9}{t10}</div>;
      $[16] = t10;
      $[17] = t9;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    return t11;
  }
  if (!conteudo) {
    let t6;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = classesDoTom("atencao");
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    const tomAtencao = t6;
    let t7;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] ${tomAtencao.icone}`}><CalendarOff aria-hidden="true" className="h-5 w-5" /></div>;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className={`flex items-center gap-3 rounded-[16px] border p-4 ${tomAtencao.caixa}`}>{t7}<div className="min-w-0 flex-1"><p className={`text-sm font-medium ${tomAtencao.texto}`}>Prazo de esclarecimento não registrado</p><p className="text-xs text-neutral-600">A sentença foi publicada e nenhum prazo foi iniciado. O controle deste processo não está no sistema.</p></div></div>;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    return t8;
  }
  const {
    Icone
  } = conteudo;
  const t6 = `flex items-center gap-3 rounded-[16px] border p-4 ${classes.caixa}`;
  const t7 = `flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] ${classes.icone}`;
  let t8;
  if ($[22] !== Icone) {
    t8 = <Icone aria-hidden="true" className="h-5 w-5" />;
    $[22] = Icone;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t7 || $[25] !== t8) {
    t9 = <div className={t7}>{t8}</div>;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const t10 = `text-sm font-medium ${classes.texto}`;
  let t11;
  if ($[27] !== conteudo.titulo || $[28] !== t10) {
    t11 = <p className={t10}>{conteudo.titulo}</p>;
    $[27] = conteudo.titulo;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== conteudo.detalhe) {
    t12 = <p className="text-xs text-neutral-600">{conteudo.detalhe}</p>;
    $[30] = conteudo.detalhe;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t11 || $[33] !== t12) {
    t13 = <div className="min-w-0 flex-1">{t11}{t12}</div>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== classes.caixa || $[36] !== classes.texto || $[37] !== conteudo.selo) {
    t14 = conteudo.selo ? <span className={`shrink-0 rounded-md px-2.5 py-1 text-xs ${classes.caixa} ${classes.texto}`}>{conteudo.selo}</span> : null;
    $[35] = classes.caixa;
    $[36] = classes.texto;
    $[37] = conteudo.selo;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t13 || $[40] !== t14 || $[41] !== t6 || $[42] !== t9) {
    t15 = <div className={t6}>{t9}{t13}{t14}</div>;
    $[39] = t13;
    $[40] = t14;
    $[41] = t6;
    $[42] = t9;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== prazo?.calendarioFeriadosAusente) {
    t16 = prazo?.calendarioFeriadosAusente ? <AvisoCalendarioAusente /> : null;
    $[44] = prazo?.calendarioFeriadosAusente;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== t15 || $[47] !== t16) {
    t17 = <div className="space-y-2">{t15}{t16}</div>;
    $[46] = t15;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  return t17;
}
function AvisoCalendarioAusente() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = classesDoTom("erro");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const tom = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AlertTriangle aria-hidden="true" className={`mt-0.5 h-4 w-4 shrink-0 ${tom.texto}`} />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className={`flex items-start gap-2.5 rounded-[16px] border p-3 ${tom.caixa}`} role="alert">{t1}<p className="text-xs text-neutral-700"><span className={`font-medium ${tom.texto}`}>Calendário de feriados do ano não cadastrado.</span>{" "}A contagem deste prazo não é confiável e a emissão de certidão está bloqueada até o cadastro.</p></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function montarConteudo({
  estado,
  prazo,
  intimadasEm,
  manifestacaoRegistradaEm,
  encerradoEm
}: Pick<FaixaEstadoPosSentencaProps, 'estado' | 'prazo' | 'intimadasEm' | 'manifestacaoRegistradaEm' | 'encerradoEm'>): Conteudo | null {
  if (estado === 'encerrado') {
    return {
      tom: 'sucesso',
      Icone: CircleCheck,
      titulo: 'Finalizado por sentença arbitral',
      detalhe: encerradoEm ? `Encerrado em ${formatarData(encerradoEm)}` : 'Encerramento registrado',
      selo: null
    };
  }
  if (estado === 'manifestacaoRecebida') {
    return {
      tom: 'marca',
      Icone: MessageSquare,
      titulo: 'Manifestação recebida — aguardando o árbitro',
      detalhe: manifestacaoRegistradaEm ? `Registrada em ${formatarData(manifestacaoRegistradaEm)}` : 'Pedido de esclarecimento registrado no prazo',
      selo: null
    };
  }
  if (!prazo) return null;
  if (estado === 'prazoVencido') {
    return {
      tom: 'erro',
      Icone: AlertTriangle,
      titulo: 'Prazo vencido sem manifestação',
      detalhe: `Venceu em ${formatarData(prazo.vencimentoEm)} · pronto para o trânsito em julgado`,
      selo: 'Ação necessária'
    };
  }
  return {
    tom: 'atencao',
    Icone: Clock,
    titulo: 'Aguardando manifestação das partes',
    detalhe: intimadasEm ? `Prazo até ${formatarData(prazo.vencimentoEm)} · intimadas em ${formatarData(intimadasEm)}` : `Prazo até ${formatarData(prazo.vencimentoEm)}`,
    selo: rotuloDiasRestantes(prazo.diasRestantes)
  };
}